import axios from "axios";
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

// Usa sempre lo stesso host del sito: https://gestionale.ethermc.it/api
// Così non rimane mai appeso a IP vecchi o URL Emergent.
export const API = "/api";

const api = axios.create({ baseURL: API, withCredentials: true });
export const apiClient = api;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = controllo sessione in corso, false = non loggato, object = user
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Evita race condition: /auth/me iniziale non deve sovrascrivere il magic-login appena riuscito.
  const authVersion = useRef(0);

  const applyLoggedUser = useCallback((nextUser) => {
    authVersion.current += 1;
    setUser(nextUser);
    setLoading(false);
  }, []);

  const refresh = useCallback(async () => {
    const versionAtStart = authVersion.current;
    try {
      const { data } = await api.get("/auth/me");
      if (authVersion.current === versionAtStart) {
        setUser(data);
      }
      return data;
    } catch {
      if (authVersion.current === versionAtStart) {
        setUser(false);
      }
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = useCallback(async (nickname, password) => {
    const { data } = await api.post("/auth/login", { nickname, password });
    applyLoggedUser(data.user);
    return data.user;
  }, [applyLoggedUser]);

  const register = useCallback(async (nickname, password) => {
    const { data } = await api.post("/auth/register", { nickname, password });
    applyLoggedUser(data.user);
    return data.user;
  }, [applyLoggedUser]);

  // IMPORTANTE: stabilizzato con useCallback. Prima cambiava ad ogni render,
  // MagicLogin rieseguiva l'effetto e consumava due volte lo stesso token:
  // 1ª chiamata = login OK, 2ª chiamata = token già usato => "Link non valido".
  const magicLogin = useCallback(async (token) => {
    const { data } = await api.post("/auth/minecraft-token", { token });
    applyLoggedUser(data.user);
    return data.user;
  }, [applyLoggedUser]);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch {}
    applyLoggedUser(false);
  }, [applyLoggedUser]);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, magicLogin, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

export function formatApiError(err) {
  const d = err?.response?.data?.detail;
  if (!d) return err?.message || "Errore di rete";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map(x => x?.msg || JSON.stringify(x)).join(" ");
  return String(d);
}
